import { NavLink, Outlet, useLocation } from "react-router-dom";
import { useEffect, useState } from "react";
import { DevTestBanner } from "./DevTestBanner";
import {
  clearSession,
  getSessionMetadata,
  getSessionToken,
  subscribeSessionMetadata,
  type SessionMetadata,
} from "../auth/session";
import { serverLogout } from "../auth/runtime";
import { SessionIdentity } from "./SessionIdentity";
import { shortAddress } from "../auth/session-claims";
import { Disclosure, Kv, KvList } from "../ui/primitives";
import { IconChevronDown, IconWallet } from "../ui/icons";

import { NAV_SECTIONS, sectionOwnsPath, type NavSection } from "./navConfig";

/** The session block that sits at the foot of the sidebar.
 *
 *  It used to print six lines of raw session state permanently — owner
 *  subject, full 42-character wallet address wrapping onto two lines, two ISO
 *  timestamps to the millisecond, and a button — which is roughly a fifth of
 *  the sidebar spent on facts an operator checks about once a session.
 *
 *  Now the resting state is one row: a wallet glyph, the owner subject, and a
 *  truncated address. Everything that was there is still there, one keystroke
 *  away, inside the disclosure. Nothing was dropped — the full address is on
 *  the `title` of the truncated one, and both timestamps are in the panel. */
function SessionFooter({ session }: { session: SessionMetadata | null }) {
  if (session === null) {
    return (
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 8,
          padding: "9px 10px",
          borderRadius: "var(--r-md)",
          border: "1px dashed var(--border)",
          color: "var(--fg-dim)",
          fontSize: 12,
        }}
      >
        <IconWallet size={15} />
        No active session
      </div>
    );
  }

  const signer = session.authority?.signerAddress ?? session.walletAddress;
  const short = shortAddress(signer);

  return (
    <Disclosure
      summary={
        <span
          style={{
            display: "inline-flex",
            alignItems: "center",
            gap: 8,
            minWidth: 0,
          }}
        >
          <IconWallet size={15} />
          <span
            style={{
              color: "var(--fg)",
              fontWeight: 600,
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {session.ownerSubject}
          </span>
          <code
            className="g-mono"
            title={signer}
            style={{ fontSize: 10, padding: "1px 5px" }}
          >
            {short}
          </code>
        </span>
      }
    >
      <SessionIdentity session={session} dense />
      <KvList dense>
        <Kv k="Wallet">
          {/* The account currently connected in the browser. Presentation and
              input only (§5) — it is NOT what authorises anything, and it can
              legitimately differ from Signer above on a legacy session. */}
          <code
            className="g-mono"
            title={session.walletAddress}
            style={{ fontSize: 11, wordBreak: "break-all" }}
          >
            {session.walletAddress}
          </code>
        </Kv>
        <Kv k="Saved">
          <code className="g-mono" style={{ fontSize: 10.5 }}>
            {new Date(session.savedAt).toISOString()}
          </code>
        </Kv>
        {session.expiresAt !== null && (
          <Kv k="Expires">
            <code className="g-mono" style={{ fontSize: 10.5 }}>
              {new Date(session.expiresAt).toISOString()}
            </code>
          </Kv>
        )}
      </KvList>
      <button
        onClick={() => {
          // Capture the bearer first: `clearSession()` makes it unreachable,
          // and the revoke needs it. Local teardown happens regardless of what
          // the server says — a failed revoke must not leave the console
          // presenting a session the operator asked to end.
          const bearer = getSessionToken();
          clearSession();
          void serverLogout(bearer).catch(() => {});
        }}
        style={{ marginTop: 12, width: "100%", fontSize: 12 }}
      >
        Sign out
      </button>
    </Disclosure>
  );
}
function LegacyPill() {
  return (
    <span
      style={{
        flex: "0 0 auto",
        fontSize: 8.5,
        fontWeight: 700,
        textTransform: "uppercase",
        letterSpacing: 0.06,
        color: "var(--warn)",
        border: "1px solid var(--warn-border)",
        background: "var(--warn-bg)",
        borderRadius: "var(--r-pill)",
        padding: "1px 6px",
      }}
    >
      legacy
    </span>
  );
}

function NavRow({
  to,
  label,
  icon: Icon,
  legacy,
  nested,
}: {
  to: string;
  label: string;
  icon?: NavSection["icon"];
  legacy?: boolean;
  nested?: boolean;
}) {
  return (
    <NavLink
      to={to}
      end
      className={({ isActive }) =>
        `nav-link${legacy ? " is-legacy" : ""}${nested ? " is-nested" : ""}${
          isActive ? " is-active" : ""
        }`
      }
    >
      {Icon && <Icon size={16} className="nav-icon" />}
      <span
        style={{
          flex: "1 1 auto",
          minWidth: 0,
          overflow: "hidden",
          textOverflow: "ellipsis",
        }}
      >
        {label}
      </span>
      {legacy && <LegacyPill />}
    </NavLink>
  );
}

/** A sidebar group.
 *
 *  Open state is local and seeded from whether the operator is currently
 *  inside the section. Deliberately NOT persisted: a sidebar that remembers
 *  every group open forever is how eighteen flat rows came back. */
function NavGroup({ section, active }: { section: NavSection; active: boolean }) {
  const [open, setOpen] = useState(active || section.defaultOpen === true);
  // Opening the section you just navigated into is the one case where the
  // sidebar should override a collapse the operator chose.
  useEffect(() => {
    if (active) setOpen(true);
  }, [active]);

  const Icon = section.icon;
  return (
    <div className="nav-group">
      <button
        type="button"
        className={`nav-group-toggle${section.legacy ? " is-legacy" : ""}`}
        aria-expanded={open}
        onClick={() => setOpen((v) => !v)}
      >
        {Icon && <Icon size={16} className="nav-icon" />}
        <span style={{ flex: "1 1 auto", textAlign: "left" }}>{section.heading}</span>
        {section.legacy && <LegacyPill />}
        <IconChevronDown
          size={13}
          className="nav-caret"
          style={{ transform: open ? "rotate(0deg)" : "rotate(-90deg)" }}
        />
      </button>
      {open && (
        <div className="nav-group-items">
          {(section.items ?? []).map((item) => (
            <NavRow
              key={item.to}
              to={item.to}
              label={item.label}
              icon={item.icon}
              legacy={item.legacy}
              nested
            />
          ))}
        </div>
      )}
    </div>
  );
}

export function Layout() {
  const [session, setSession] = useState<SessionMetadata | null>(
    getSessionMetadata(),
  );
  useEffect(() => subscribeSessionMetadata(setSession), []);
  const { pathname } = useLocation();

  return (
    <div className="app-shell">
      <aside className="app-sidebar">
        <div className="app-sidebar-inner">
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 9,
              padding: "2px 10px 12px",
            }}
          >
            <span
              aria-hidden="true"
              style={{
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                width: 26,
                height: 26,
                borderRadius: 7,
                background: "var(--accent)",
                color: "var(--accent-fg)",
                fontWeight: 800,
                fontSize: 13,
                letterSpacing: -0.03,
                flex: "0 0 auto",
              }}
            >
              G
            </span>
            <span style={{ fontWeight: 660, fontSize: 14.5, letterSpacing: -0.01 }}>
              Gao Admin
            </span>
          </div>

          <DevTestBanner compact />

          <nav className="app-sidebar-nav" aria-label="Console sections">
            {NAV_SECTIONS.map((section) =>
              section.items === undefined && section.to !== undefined ? (
                <NavRow
                  key={section.heading}
                  to={section.to}
                  label={section.heading}
                  icon={section.icon}
                  legacy={section.legacy}
                />
              ) : (
                <NavGroup
                  key={section.heading}
                  section={section}
                  active={sectionOwnsPath(section, pathname)}
                />
              ),
            )}
          </nav>

          <div style={{ flex: 1, minHeight: 12 }} />

          <div style={{ paddingTop: 10, borderTop: "1px solid var(--border-faint)" }}>
            <SessionFooter session={session} />
          </div>
        </div>
      </aside>

      <main className="app-main">
        <div className="app-canvas">
          <Outlet />
        </div>
      </main>
    </div>
  );
}
